import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import {
  ArrowDownWideNarrowIcon,
  GitBranchIcon,
  LayoutGrid,
  PackageIcon,
} from 'lucide-react';

import {
  EdgeGroupListItemResponse,
  useEdgeGroups,
} from '@/react/edge/edge-groups/queries/useEdgeGroups';
import { useSources } from '@/react/portainer/gitops/sources/queries/useSources';
import { SourceId } from '@/react/portainer/gitops/sources/types';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { useEnvironmentList } from '@/react/portainer/environments/queries/useEnvironmentList';
import { Environment } from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { Card } from '@@/primitives/Card';
import { Badge } from '@@/Badge';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { Alert } from '@@/Alert';

import { reviewStepValidationSchema } from '../validation';
import { WizardStep } from '../WizardContext';
import { Artifact, FormValues } from '../types';
import { DEPLOYMENT_TYPE_LABELS } from '../detectDeploymentType';
import { deriveDeployOrder } from '../deployOrder';

import { StepTitle } from './StepTitle';

export const ReviewStep: WizardStep = {
  id: 'review',
  label: 'Review',
  Component: ReviewStepView,
  validateStep,
};

function ReviewStepView() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const sourcesQuery = useSources({ type: 'git' });
  const sourceNameById = useMemo(
    () =>
      new Map(
        sourcesQuery.data?.data?.map((source) => [source.id, source.name])
      ),
    [sourcesQuery.data]
  );
  const sources = values.sources
    .map((sourceId) => ({ id: sourceId, name: sourceNameById.get(sourceId) }))
    .filter((source): source is { id: SourceId; name: string } =>
      Boolean(source.name)
    );
  const edgeGroupsQuery = useEdgeGroups();
  const deployOrder = deriveDeployOrder(values.artifacts);
  const orderedArtifacts = toOrderedArtifacts(values.artifacts, deployOrder);
  const environmentGroupsQuery = useGroups();

  // The reconciliation engine can name environments directly, so their names are
  // resolved here rather than per artifact, which would be a request each.
  const namedEnvironments = values.artifacts.flatMap(
    (artifact) => artifact.environments
  );
  const { environments } = useEnvironmentList(
    { endpointIds: namedEnvironments, pageLimit: 0 },
    { enabled: namedEnvironments.length > 0 }
  );

  return (
    <>
      <StepTitle
        title="Review & name your workflow"
        subtitle="Give this workflow a name and confirm the sources, stacks, and deployments targets below. Everything is created together when you finish."
      />

      <div className="flex flex-col gap-4">
        <FormControl inputId="workflow-name" label="Workflow name" required>
          <Input
            id="workflow-name"
            onChange={(e) => {
              setFieldValue('name', e.target.value);
            }}
            placeholder="my-workflow"
            value={values.name}
            data-cy="workflow-name-input"
            required
          />
        </FormControl>

        <Card.Container aria-label="Sources">
          <Card.Header icon={GitBranchIcon} title="Sources" />
          <Card.Body className="flex flex-col gap-1">
            {sources.map((source) => (
              <span key={source.id}>{source.name}</span>
            ))}
          </Card.Body>
        </Card.Container>

        {values.artifacts.length > 1 && (
          <DeployOrderSection result={deployOrder} />
        )}

        {orderedArtifacts.map((artifact) => (
          <ArtifactReviewSection
            key={artifact.name}
            values={artifact}
            edgeGroups={edgeGroupsQuery.data}
            environmentGroups={environmentGroupsQuery.data}
            environments={environments}
            sourceNameById={sourceNameById}
          />
        ))}
      </div>
    </>
  );
}

/** Falls back to the authored order when a cycle makes deploy order undefined. */
function toOrderedArtifacts(
  artifacts: Array<Artifact>,
  result: ReturnType<typeof deriveDeployOrder>
): Array<Artifact> {
  if (result.hasCycle) {
    return artifacts;
  }

  return result.order
    .map((name) => artifacts.find((artifact) => artifact.name === name))
    .filter((artifact): artifact is Artifact => !!artifact);
}

function DeployOrderSection({
  result,
}: {
  result: ReturnType<typeof deriveDeployOrder>;
}) {
  return (
    <Card.Container aria-label="Deploy order">
      <Card.Header
        icon={ArrowDownWideNarrowIcon}
        title="Deploy order"
        subtitle="Derived from each artifact's dependencies."
      />
      <Card.Body>
        {result.hasCycle ? (
          <Alert color="error">
            Circular dependency detected among: {result.cyclicNames.join(', ')}.
            Fix the dependencies above before continuing.
          </Alert>
        ) : (
          <ol className="flex list-decimal flex-col gap-1 pl-4">
            {result.order.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ol>
        )}
      </Card.Body>
    </Card.Container>
  );
}

function ArtifactReviewSection({
  values,
  edgeGroups,
  environmentGroups,
  environments,
  sourceNameById,
}: {
  values: Artifact;
  edgeGroups: EdgeGroupListItemResponse[] | undefined;
  environmentGroups: EnvironmentGroup[] | undefined;
  environments: Array<Environment>;
  sourceNameById: Map<SourceId, string>;
}) {
  // All three kinds an artifact can name, not only the Edge groups an Edge stack
  // is limited to: a reconciled artifact naming an environment group would
  // otherwise review as having no targets at all.
  const targetNames = [
    ...(edgeGroups ?? [])
      .filter((group) => values.targets.includes(group.Id))
      .map((group) => group.Name),
    ...(environmentGroups ?? [])
      .filter((group) => values.environmentGroups.includes(group.Id))
      .map((group) => group.Name),
    ...environments
      .filter((environment) => values.environments.includes(environment.Id))
      .map((environment) => environment.Name),
  ];

  return (
    <Card.Container aria-label="Artifact">
      <Card.Header
        icon={PackageIcon}
        title="Artifact"
        subtitle={values.name}
        actions={
          <Badge type="muted">
            {DEPLOYMENT_TYPE_LABELS[values.deploymentType]}
          </Badge>
        }
      />
      <Card.Body className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          {values.files.map((file) => {
            const sourceName = sourceNameById.get(file.sourceId);
            return (
              <span key={file.path} className="flex items-baseline gap-2">
                {sourceName && (
                  <span className="text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
                    {sourceName} @ {file.ref}
                  </span>
                )}
                <span className="font-mono">/{file.path}</span>
              </span>
            );
          })}
          {values.dependencies.length > 0 && (
            <p className="text-xs text-gray-6 th-highcontrast:text-gray-5 th-dark:text-gray-5">
              Depends on: {values.dependencies.join(', ')}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-gray-6 uppercase th-highcontrast:text-gray-5 th-dark:text-gray-5">
            <LayoutGrid size={12} />
            Targets
            <Badge type="muted">{targetNames.length}</Badge>
          </div>
          {targetNames.map((name) => (
            <span key={name}>{name}</span>
          ))}
        </div>
      </Card.Body>
    </Card.Container>
  );
}

function validateStep() {
  return reviewStepValidationSchema();
}
